import AppApi from './api'

interface AgentStatusResponse {
  awsUserId: string
  currentStatus: {
    agentStatusId: string
    name: string
    type: string
  } | null
  agentName: string
  lastStatusChange: string | null
  status: string
  ivrText: string
}

interface SetAgentStatusRequest {
  awsUserId: string
  status: boolean
}

interface SetAgentStatusResponse {
  awsUserId: string
  newStatus: string
  message: string
  status: string
  ivrText: string
}

export const agentStatusApi = AppApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentAgentStatus: builder.query<AgentStatusResponse, void>({
      query: () => ({
        url: '/aws-connect/agents/status/current',
        method: 'GET',
      }),
      // Don't transform error response - let it be handled by the component
      // so we can detect HTTP 500 status code
      providesTags: ['AgentStatus'],
    }),
    setAgentStatus: builder.mutation<SetAgentStatusResponse, {status: boolean}>({
      query: (statusData) => ({
        url: '/aws-connect/agents/status',
        method: 'POST',
        body: statusData,
      }),
      // Remove invalidatesTags to prevent automatic refetch
      // We'll handle the cache update manually for faster response
      async onQueryStarted(arg, {dispatch, queryFulfilled, getState}) {
        try {
          const {data} = await queryFulfilled
          console.log('📥 POST API Response received:', data)

          if (data.status === '200') {
            // Get existing cache data if it exists
            const existingData = agentStatusApi.endpoints.getCurrentAgentStatus.select()(getState())
            const currentCacheData = existingData?.data
            
            // Use upsertQueryData to create or update the cache entry
            // This works even if there's no existing cache entry (like after a 500 error)
            dispatch(
              agentStatusApi.util.upsertQueryData('getCurrentAgentStatus', undefined, {
                awsUserId: data.awsUserId || currentCacheData?.awsUserId || '',
                status: '200',
                currentStatus: {
                  agentStatusId: currentCacheData?.currentStatus?.agentStatusId || '',
                  name: data.newStatus === 'Available' ? 'Available' : 'Offline',
                  type: data.newStatus === 'Available' ? 'ROUTABLE' : 'OFFLINE',
                },
                agentName: currentCacheData?.agentName || '',
                lastStatusChange: new Date().toISOString(),
                ivrText: data.ivrText || currentCacheData?.ivrText || '',
              })
            )
            
            console.log('✅ Cache upserted based on POST API response:', {
              newStatus: data.newStatus,
              currentStatusType: data.newStatus === 'Available' ? 'ROUTABLE' : 'OFFLINE',
              status: '200',
              updatedCache: {
                status: '200',
                currentStatus: {
                  name: data.newStatus === 'Available' ? 'Available' : 'Offline',
                  type: data.newStatus === 'Available' ? 'ROUTABLE' : 'OFFLINE',
                }
              }
            })
          } else {
            console.warn('⚠️ Response status is not 200:', data.status)
          }
        } catch (error: any) {
          console.error('❌ Error in onQueryStarted:', error)
          
          // Check if the error is "already in requested status" - treat as success
          const errorMessage = error?.data?.message || error?.message || ''
          if (errorMessage.includes('already in requested status') || errorMessage.includes('User already in requested status')) {
            console.log('✅ User already in requested status - updating cache as success')
            
            // Get existing cache data if it exists
            const existingData = agentStatusApi.endpoints.getCurrentAgentStatus.select()(getState())
            const currentCacheData = existingData?.data
            
            // Update cache with the requested status (treat as success)
            const requestedStatus = arg.status ? 'Available' : 'Offline'
            dispatch(
              agentStatusApi.util.upsertQueryData('getCurrentAgentStatus', undefined, {
                awsUserId: currentCacheData?.awsUserId || '',
                status: '200',
                currentStatus: {
                  agentStatusId: currentCacheData?.currentStatus?.agentStatusId || '',
                  name: requestedStatus,
                  type: arg.status ? 'ROUTABLE' : 'OFFLINE',
                },
                agentName: currentCacheData?.agentName || '',
                lastStatusChange: new Date().toISOString(),
                ivrText: 'Agent status is already set to ' + requestedStatus,
              })
            )
            
            console.log('✅ Cache updated for "already in requested status" case:', {
              requestedStatus: requestedStatus,
              statusType: arg.status ? 'ROUTABLE' : 'OFFLINE',
            })
          }
          // For other errors, the cache will remain unchanged
          // and the UI will show the previous state
        }
      },
    }),
  }),
  overrideExisting: false,
})

export const {useGetCurrentAgentStatusQuery, useSetAgentStatusMutation} = agentStatusApi
